import React, { useEffect, useState } from 'react';
import PageContainer from '../../components/PageContainer';
import MainContent from '../../components/MainContent';
import PageTitle from '../../components/Common/PageTitle/PageTitle';
import { useAccounts } from '../../DataStore/AccountsContext';
import ActivityLoader from '../../components/Common/ActivityLoader';
import Notification from '../../components/Common/Notification';
import TreeNode from '../../components/Account/TreeNode';
import ActionDropdownItem from '../../components/Common/PageTitle/ActionDropdownItem';
import { useTheme } from '../../DataStore/ThemeContext';
import NewOpeningBalance from '../../components/Account/NewOpeningBalance';
import NewAccountModal from '../../components/Account/NewAccountModal';

const AccountsDashboard = () => {
    const { getChartOfAccounts, chartOfAccounts, setChartofAccounts, setExpanded, expanded } = useAccounts();
    const { openSettings } = useTheme();
    const [showNewAccountModal, setShowNewAccountModal] = useState(false);

    const [searchQuery, setSearchQuery] = useState(''); // Search input state
    const [filteredAccounts, setFilteredAccounts] = useState([]); // Filtered accounts

    useEffect(() => {
        getChartOfAccounts();
    }, []);

    useEffect(()=>{
        setFilteredAccounts(chartOfAccounts);
    }, [chartOfAccounts])

    useEffect(()=>{
        if(Array.isArray(chartOfAccounts)){
            toggleExpandAll(expanded)
        }
    }, [expanded])

    useEffect(() => {
        handleSearch(); // Filter accounts whenever the search query changes
    }, [searchQuery]);

    // Toggle all nodes
    const toggleExpandAll = (expand) => {
        const toggleNodes = (nodes) =>
            nodes.map((node) => ({
                ...node,
                isExpanded: expand,
                children: node.children ? toggleNodes(node.children) : [],
            }));
        const updatedAccounts = toggleNodes(chartOfAccounts);
        setChartofAccounts(updatedAccounts);
        setFilteredAccounts(filterTree(updatedAccounts, searchQuery.toLowerCase())); // Ensure filtered data reflects changes
    };

    // Toggle individual node
    const toggleNode = (nodeId) => {
        const updateNodes = (nodes) =>
            nodes.map((node) => ({
                ...node,
                isExpanded: node.id === nodeId ? !node.isExpanded : node.isExpanded,
                children: node.children ? updateNodes(node.children) : [],
            }));
        const updatedAccounts = updateNodes(chartOfAccounts);
        setChartofAccounts(updatedAccounts);
        setFilteredAccounts(filterTree(updatedAccounts, searchQuery.toLowerCase())); // Ensure filtered data reflects changes
    };

    // Recursive function to filter the tree structure
    const filterTree = (nodes, query) => {
        return nodes
            .map((node) => {
                if (
                    node.account_number.toLowerCase().includes(query) ||
                    node.account_name.toLowerCase().includes(query)
                ) {
                    return node; // Include the node if it matches the query
                }

                if (node.children) {
                    const filteredChildren = filterTree(node.children, query);
                    if (filteredChildren.length > 0) {
                        return { ...node, children: filteredChildren }; // Include parent if children match
                    }
                }

                return null; // Exclude non-matching nodes
            })
            .filter((node) => node !== null); // Remove null entries
    };

    // Handle search query changes
    const handleSearch = () => {
        if (searchQuery) {
            setFilteredAccounts(filterTree(chartOfAccounts, searchQuery.toLowerCase()));
        } else {
            setFilteredAccounts(chartOfAccounts); // Reset to original tree if query is empty
        }
    };

    const PageActions = () => (
        <>
            <ActionDropdownItem
                iconClass="fa fa-edit"
                label="New Account"
                onClick={() => setShowNewAccountModal(true)}
            />
            <ActionDropdownItem iconClass="fa fa-file-import" label="Import from file..." />
            <ActionDropdownItem iconClass="fa fa-sync" label="Refresh" />
        </>
    );


    return (
        <>
            <PageContainer>
                <MainContent>
                    <PageTitle
                        title="Chart of Accounts"
                        description="List of all organisation accounts accordingly to hierarchy"
                        iconClass="fa fa-project-diagram"
                        sticky={true}
                        PageActions={PageActions}
                    />

                    <div className="card mb-3">
                        <div className="card-header">
                            <div className="card-header-title font-size-lg text-capitalize font-weight-normal">
                                Accounts Listing
                            </div>
                            <div className="btn-actions-pane-right d-flex align-items-center">
                               
                                {/* Expand/Collapse Button */}
                                <button
                                    type="button"
                                    className="btn-icon btn-wide btn-outline-2x btn btn-outline-focus btn-sm d-flex"
                                    onClick={() => setExpanded(!expanded)}
                                >
                                    {expanded ? 'Collapse All' : 'Expand All'}
                                    <span className="pl-2 align-middle opacity-7">
                                        <i
                                            className={`expand-ico ${
                                                expanded ? 'fa fa-folder' : 'fa-regular fa-folder-open'
                                            }`}
                                        ></i>
                                    </span>
                                </button>
                            </div>
                        </div>

                        <div className="card-body">
                        <div className="mb-4">

                             <div className="search-container">
                                <i className="fa fa-filter search-icon"></i>
                                <input
                                    type="text"
                                    className="form-control search-input"
                                    placeholder="Search by account number or account title..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                        </div>
                            <div className="tree-container">
                                {Array.isArray(filteredAccounts) && filteredAccounts.length > 0 ? (
                                    filteredAccounts.map((node) => (
                                        <TreeNode
                                            key={node.id}
                                            node={node}
                                            isExpanded={node.isExpanded}
                                            toggleNode={toggleNode}
                                        />
                                    ))
                                ) : (
                                    <Notification
                                        type="light"
                                        message="No accounts match your search criteria."
                                    />
                                )}
                                {!chartOfAccounts && <ActivityLoader />}
                                {chartOfAccounts === '404' && (
                                    <Notification type="light" message="Chart of accounts could not be loaded" />
                                )}
                            </div>
                        </div>
                    </div>
                </MainContent>
            </PageContainer>
            <NewOpeningBalance />
            <NewAccountModal
                show={showNewAccountModal}
                onClose={() => setShowNewAccountModal(false)}
            />
        </>
    );
};

export default AccountsDashboard;
